import React, { useState } from "react";
import { toast } from "react-toastify";
import { BadgeCheck, MoreHorizontal } from "lucide-react";
import { createCampaign, createAdSet, createAdSetTargeting, createAd, submitAd } from "../../hook/useAdsApi";
import { AD_OBJECTIVES, CTA_ICONS, PLACEMENTS, daysBetween, formatSubmittedAt } from "./adsConstants";
import profileImg from "../../assets/profile.png";

function getCurrentUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user"));
  } catch {
    return null;
  }
}

export default function AdFinalPreview({ formData, adAccount, onSubmitted }) {
  const [submitting, setSubmitting] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  // Tracks which records a previous, failed submit attempt already created, so retrying
  // (without navigating away, which would remount this component and reset this) doesn't
  // create duplicate campaigns/ad sets/ads. Resets naturally if the user goes Back to edit
  // form data and returns here - which is correct, since a resubmit after an edit should
  // create fresh records reflecting the new values, not reuse stale ones.
  const [progress, setProgress] = useState({ campaign: null, adSet: null, ad: null, targetingDone: false });
  const coverImage = formData.mediaFiles[0]?.url;
  const objective = AD_OBJECTIVES.find((o) => o.value === formData.objective);
  const ctaIcon = CTA_ICONS[formData.cta];
  const user = getCurrentUser();
  const posterName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : "";
  const avatarUrl = sessionStorage.getItem("profilePictureUrl") || profileImg;

  // budget/budget_type are sent as entered: the raw value is the daily cap in Daily mode or
  // the lifetime total in Lifetime mode. dailyAmount/totalBudget below are display-only
  // derivations, not what gets sent to the API.
  const days = daysBetween(formData.startDate, formData.endDate) || 1;
  const isDaily = formData.budgetType === "daily";
  const dailyAmount = isDaily ? Number(formData.budget) : Number(formData.budget) / days;
  const totalBudget = isDaily ? Number(formData.budget) * days : Number(formData.budget);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Campaign starts Draft, not Active: it should only go Active once the first ad in it
      // is Approved by admin moderation - that transition happens on the backend/admin side,
      // not here. Each record is only created once per mount: if a previous attempt already
      // created it (tracked in `progress`), it's reused instead of creating a duplicate on retry.
      const campaign = progress.campaign || await createCampaign(adAccount.id, {
        name: formData.campaignName,
        objective: formData.objective,
        budget: Number(formData.budget),
        budget_type: formData.budgetType,
        start_date: formData.startDate,
        end_date: formData.endDate,
        status: "Draft",
      });
      if (!progress.campaign) setProgress((p) => ({ ...p, campaign }));

      const adSet = progress.adSet || await createAdSet(campaign.id, {
        name: formData.adSetName,
        target_type: formData.targetType,
        target_id: formData.targetId || undefined,
        status: "Draft",
      });
      if (!progress.adSet) setProgress((p) => ({ ...p, adSet }));

      if (!progress.targetingDone) {
        try {
          await createAdSetTargeting(adSet.id, {
            min_age: formData.minAge,
            max_age: formData.maxAge,
            genders: [formData.gender],
            // Unlike genders, the backend's device list has no "all" value - omitting the field
            // entirely means "no device restriction", which is what "All" is meant to convey.
            ...(formData.devices !== "all" && { devices: [formData.devices] }),
            interest_ids: formData.interests.map((i) => i.id),
            seller_relationship: { followers: formData.connectToSeller },
            previous_brand_buyers: formData.previousBrandBuyers,
          });
          setProgress((p) => ({ ...p, targetingDone: true }));
        } catch (targetingError) {
          // The campaign/ad set already exist at this point - don't block the rest of
          // submission over the targeting save failing, just surface a warning. Left
          // un-flagged as done so a genuine failure is retried on the next attempt.
          toast.warning(targetingError.response?.data?.message || "Failed to save ad set targeting");
        }
      }

      const selectedPlacements = PLACEMENTS.filter((p) => formData.placements[p.key]);
      // delivery_config is what the buyer-facing auction engine (ad-auction.service.js) reads
      // for eligibility - only include it when at least one placement was picked, since the
      // backend requires maximum_bid + a non-empty placements array whenever it's provided at
      // all. maximum_bid is a placeholder heuristic (same as promoteListing's) - a fraction of
      // the campaign's daily budget, never surfaced to the seller since this UI doesn't expose
      // real bidding yet.
      const deliveryConfig = selectedPlacements.length
        ? {
            maximum_bid: Math.max(0.01, Number((dailyAmount * 0.05).toFixed(6))),
            placements: selectedPlacements.map((p) => p.deliveryValue),
          }
        : undefined;

      const ad = progress.ad || await createAd(adSet.id, {
        name: formData.adName,
        creative_type: formData.creativeType,
        creative_media: formData.mediaFiles,
        // thumbnailSchema requires media_type:'image' strictly, and client-api separately
        // requires a thumbnail outright for video creatives (the video file can't serve as
        // its own thumbnail) - so video sends the dedicated poster image collected in
        // CreateAd.js, image sends the media itself (both satisfy the 'image' requirement).
        thumbnail: formData.creativeType === "video" ? formData.thumbnail : formData.mediaFiles[0],
        headline: formData.headline,
        body_text: formData.description,
        cta: formData.cta,
        destination_type: formData.destinationType,
        destination_id: formData.destinationId || undefined,
        ...(deliveryConfig && { delivery_config: deliveryConfig }),
      });
      if (!progress.ad) setProgress((p) => ({ ...p, ad }));

      const submitted = await submitAd(ad.id);
      toast.success("Ad campaign submitted successfully!");
      onSubmitted({ ad: submitted || ad, campaign });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to submit ad campaign");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Exact layout/colors from the Figma "Ad Final Preview" screen (read-only access,
          matched from a shared screenshot). Engagement count and multi-image carousel from
          the Figma mock aren't shown here - this ad hasn't been submitted yet, so there's no
          real comment count, and creative_media only ever holds one item (no carousel). */}
      <div className="rounded-[26px] p-4 mb-6 bg-[#fefdeb]">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <img src={avatarUrl || profileImg} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-sm text-[#131b2e]">{posterName || "Your Business"}</p>
                {adAccount?.status === "Active" && (
                  <span className="flex items-center gap-1 border border-[#0891b2] text-[#0891b2] text-[10px] font-semibold px-2 py-0.5 rounded-full">
                    <BadgeCheck size={12} /> Verified Business
                  </span>
                )}
              </div>
              <p className="text-xs text-[#747474]">{formatSubmittedAt(new Date().toISOString())}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="bg-[#ff8a3d] text-white text-[10px] font-bold px-2 py-1 rounded-full">Ad</span>
            <span className="w-7 h-7 rounded-full border border-[#c3c5d9] flex items-center justify-center">
              <MoreHorizontal size={16} className="text-[#434656]" />
            </span>
          </div>
        </div>

        <p className={`text-sm text-[#434656] mb-3 ${descExpanded ? "" : "line-clamp-3"}`}>
          {formData.description}{" "}
          {!descExpanded && formData.description?.length > 140 && (
            <button
              type="button"
              onClick={() => setDescExpanded(true)}
              className="text-[#0891b2] font-bold text-sm"
            >
              Read More
            </button>
          )}
        </p>

        {coverImage && (
          formData.mediaFiles[0]?.media_type === "video" ? (
            <video src={coverImage} controls className="w-full h-64 object-cover rounded-xl mb-3" />
          ) : (
            <img src={coverImage} alt="" className="w-full h-64 object-cover rounded-xl mb-3" />
          )
        )}

        <div className="flex items-center justify-end">
          <button
            type="button"
            className="flex items-center gap-2 bg-[#4dd0e1] text-white text-sm font-semibold px-4 py-2 rounded-full"
          >
            {ctaIcon && <img src={ctaIcon} alt="" className="w-4 h-4" />}
            {formData.cta ? formData.cta.replace(/_/g, " ") : "Learn More"}
          </button>
        </div>
      </div>

      {objective && (
        <div className="flex items-center gap-3 mb-4">
          {objective.iconSrc && <img src={objective.iconSrc} alt="" className="w-10 h-10" />}
          <p className="font-semibold text-[#131b2e]">{objective.label}</p>
        </div>
      )}

      <label className="block text-sm font-semibold text-black mb-1">Headline</label>
      <p className="text-[#434656] mb-4">{formData.headline}</p>

      <label className="block text-sm font-semibold text-black mb-1">Description</label>
      <p className="text-[#434656] mb-4">{formData.description}</p>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm font-semibold text-black">Daily budget</p>
          <p className="text-[#434656]">$ {dailyAmount.toFixed(2)} / Day</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-black">Schedule</p>
          <p className="text-[#434656]">
            {formData.startDate} - {formData.endDate}
          </p>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-sm font-semibold text-black">Estimated Total Spend</p>
        <p className="text-[#434656]">$ {totalBudget.toFixed(2)} / Over {days} Days</p>
      </div>

      <button
        type="button"
        disabled={submitting}
        onClick={handleSubmit}
        className="w-full bg-[#095228] text-white font-semibold py-4 rounded-full disabled:opacity-50"
      >
        {submitting ? "Submitting..." : "Submit for Review"}
      </button>
    </div>
  );
}
